import "./SpendAndEffectBarChart.scss";
import React, { useMemo, useState } from "react";
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import cn from "classnames";
import { capitalizeWords, getBarChartContributorsData } from "../MMMUtils";
import { Setter } from "../../utils/types";
import { MdOutlineWarningAmber } from "react-icons/md";
import { ReactComponent as DemandCaptureIcon } from "../../Components/VisualLegendHorizontal/DemandCapture.svg";
import { ReactComponent as DemandGenIcon } from "../../Components/VisualLegendHorizontal/DemandGen.svg";
import { OverlayTrigger } from "../../Components";
import { Tooltip as BootstrapTooltip } from "react-bootstrap";
import {
  DEFAULT_SORT_OPTIONS,
  DEMAND_GEN_VS_CAP,
  SINGLE_COLOR_SORT_OPTIONS,
} from "../../Components/SpendAndEffectModal";

interface SpendAndEffectBarChartProps {
  barData: any[];
  data: any[];
  setBarData?: Setter<any>;
  displayActualValues: boolean;
  valueFormatter: (value: number) => string;
  activeChannel: string;
  setActiveChannel?: Setter<string>;
  className?: string;
  colorMap: {
    [key: string]: string;
  };
  expandedModal: boolean | undefined;
  barSize: number;
  setXAxisScale?: Setter<number>;
  xAxisScale?: number;
  groupBy: string;
}

const genToolTipText = (perc: number, chan: string, type: string | boolean): string => {
  const from = type === "direct" ? "Direct" : type ? "Induced by" : "Attributed to";
  return `${perc.toFixed(1)}% ${from} ${chan}`;
};

const BAR_MAP = [
  {
    dataKey: "value",
    stackId: "spendModal",
    opacity: 1,
  },
  {
    dataKey: "value2",
    stackId: "revenueModal",
    opacity: 1,
  },
  {
    dataKey: "twoStageSpend",
    stackId: "spendModal",
    opacity: 1,
  },
  {
    dataKey: "twoStageEffect",
    stackId: "revenueModal",
    opacity: 1,
  },
];

export const SpendAndEffectBarChart: React.FC<SpendAndEffectBarChartProps> = ({
  barData,
  setBarData,
  displayActualValues,
  valueFormatter,
  setActiveChannel,
  activeChannel,
  data,
  className = "",
  colorMap,
  expandedModal = false,
  barSize,
  setXAxisScale,
  xAxisScale,
  groupBy,
}) => {
  const [tooltipText, setTooltipText] = useState<string>("");

  const dynamicBars = useMemo(
    () =>
      BAR_MAP.reduce((acc: any[], el, i) => {
        el.dataKey === "twoStageSpend" || el.dataKey === "twoStageEffect"
          ? data.forEach((channel, j) => {
              const type = el.dataKey === "twoStageSpend" ? "Spend" : "Effect";
              acc.push(
                <Bar
                  key={`subModalBar${type + j}`}
                  barSize={barSize}
                  dataKey={`${channel.name}:${type}`}
                  isAnimationActive={false}
                  opacity={el.opacity}
                  stackId={el.stackId}
                  yAxisId={0}
                >
                  {barData.map(item => (
                    <Cell
                      key={`subModalCell${item.name}${i}`}
                      fill={
                        item.added
                          ? `url(#shadedStripeNeg${
                              SINGLE_COLOR_SORT_OPTIONS.includes(activeChannel) ||
                              channel.name === activeChannel
                                ? colorMap[item.name]
                                : item.name === activeChannel || activeChannel === DEMAND_GEN_VS_CAP
                                ? colorMap[channel.name]
                                : "grey"
                            })`
                          : `url(#shadedStripe${
                              SINGLE_COLOR_SORT_OPTIONS.includes(activeChannel) ||
                              channel.name === activeChannel
                                ? colorMap[item.name]
                                : item.name === activeChannel || activeChannel === DEMAND_GEN_VS_CAP
                                ? colorMap[channel.name]
                                : "grey"
                            })`
                      }
                      stroke="white"
                      strokeWidth={expandedModal ? 2 : 0}
                      onClick={
                        expandedModal &&
                        setBarData &&
                        setActiveChannel &&
                        activeChannel !== DEMAND_GEN_VS_CAP
                          ? () => {
                              setBarData(getBarChartContributorsData(data, item.contributors));
                              setActiveChannel(item.name);
                            }
                          : undefined
                      }
                      onMouseOver={
                        expandedModal
                          ? () =>
                              setTooltipText(
                                genToolTipText(
                                  item[`${channel.name}:${type}`],
                                  channel.name,
                                  item.added
                                )
                              )
                          : undefined
                      }
                    />
                  ))}
                </Bar>
              );
            })
          : acc.push(
              <Bar
                key={`modalBar${i}`}
                barSize={barSize}
                dataKey={el.dataKey}
                isAnimationActive={false}
                opacity={el.opacity}
                stackId={el.stackId}
                yAxisId={0}
              >
                {barData.map(item => (
                  <Cell
                    key={`modalcell${item.name}${i}`}
                    fill={
                      DEFAULT_SORT_OPTIONS.includes(activeChannel) || activeChannel === item.name
                        ? colorMap[item.name]
                        : "grey"
                    }
                    onClick={
                      expandedModal &&
                      setBarData &&
                      setActiveChannel &&
                      activeChannel !== DEMAND_GEN_VS_CAP
                        ? () => {
                            setBarData(getBarChartContributorsData(data, item.contributors));
                            setActiveChannel(item.name);
                          }
                        : undefined
                    }
                    onMouseOver={
                      expandedModal
                        ? () =>
                            setTooltipText(genToolTipText(item[el.dataKey], item.name, "direct"))
                        : undefined
                    }
                    stroke="white"
                    strokeWidth={expandedModal ? 2 : 0}
                  />
                ))}
              </Bar>
            );
        return acc;
      }, []),
    [activeChannel, barData, barSize, colorMap, data, expandedModal, setActiveChannel, setBarData]
  );

  const outlineBarProperties = useMemo(() => {
    return expandedModal
      ? {
          barSize: barSize - 2,
          stroke: "#4e4e50",
          strokeWidth: 2,
          getFill: () => "transparent",
          opacity: 1,
        }
      : {
          barSize: barSize,
          stroke: "transparent",
          strokeWidth: 0,
          getFill: name => colorMap[name],
          opacity: 0.5,
        };
  }, [barSize, colorMap, expandedModal]);

  return (
    <div className={`SpendAndEffectBarChart ${className}`}>
      <SpendAndEffectLegend
        barData={barData}
        displayActualValues={displayActualValues}
        expandedModal={expandedModal}
        onClick={(contributors, name) =>
          expandedModal && setActiveChannel && setBarData
            ? () => {
                setBarData(getBarChartContributorsData(data, contributors));
                setActiveChannel(name);
              }
            : undefined
        }
        valueFormatter={valueFormatter}
        groupBy={groupBy}
      />
      <ResponsiveContainer className="rechartContainer" width="100%" height={barData.length * 70}>
        <BarChart layout="vertical" barCategoryGap={6} barGap={8} data={barData}>
          <XAxis
            type="number"
            domain={
              expandedModal && setXAxisScale
                ? [
                    0,
                    dataMax => {
                      if (!xAxisScale || dataMax > xAxisScale) {
                        setXAxisScale(dataMax);
                        return dataMax;
                      } else {
                        return xAxisScale;
                      }
                    },
                  ]
                : [0, "auto"]
            }
            hide
          />
          <YAxis type="category" dataKey="value" yAxisId={0} hide />
          <YAxis type="category" dataKey="value" yAxisId={1} hide />
          {expandedModal && (
            <RechartsTooltip
              shared={false}
              content={() => <div className="barChartTooltip">{tooltipText}</div>}
              cursor={{ fill: "transparent" }}
            />
          )}
          {expandedModal && dynamicBars}
          <Bar
            key="spendOutlineBar"
            barSize={outlineBarProperties.barSize}
            dataKey="totalActualizedSpend"
            isAnimationActive={false}
            opacity={1}
            stroke={outlineBarProperties.stroke}
            strokeWidth={outlineBarProperties.strokeWidth}
            radius={2}
            yAxisId={1}
            style={{ pointerEvents: "none" }}
          >
            {barData.map(({ name }) => (
              <Cell key={name} fill={outlineBarProperties.getFill(name)} />
            ))}
          </Bar>
          <Bar
            key={"effectOutlineBar"}
            barSize={outlineBarProperties.barSize}
            dataKey={"totalActualizedEffect"}
            isAnimationActive={false}
            opacity={outlineBarProperties.opacity}
            stroke={outlineBarProperties.stroke}
            strokeWidth={outlineBarProperties.strokeWidth}
            radius={2}
            yAxisId={1}
            style={{ pointerEvents: "none" }}
          >
            {barData.map(({ name }) => (
              <Cell key={name} fill={outlineBarProperties.getFill(name)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

interface SpendAndEffectLegendProps {
  barData: any[];
  displayActualValues: boolean;
  expandedModal: boolean;
  onClick?: (contributors: any, name: string) => void;
  valueFormatter: (value: number) => string;
  groupBy?: string;
}

const SpendAndEffectLegend: React.FC<SpendAndEffectLegendProps> = ({
  barData,
  displayActualValues,
  expandedModal,
  onClick,
  valueFormatter,
  groupBy = "name",
}) => (
  <div
    className={cn("chartLegend", {
      hideActualValues: !displayActualValues,
      showActualValues: displayActualValues,
      expanded: expandedModal,
      collapsed: !expandedModal,
    })}
  >
    {barData.map((item, index) => {
      const {
        name,
        displayName,
        channelName,
        platformName,
        tacticName,
        brandNonBrandName,
        totalActualizedSpend,
        totalActualizedEffect,
        incrementality,
        totalActualizedSpendRawValueLabel,
        totalActualizedEffectRawValueLabel,
        totalEffectForChannelLabel,
        totalSpendForChannelLabel,
        contributorsSpendSum,
        contributorsEffectSum,
        contributors,
        added,
      } = item;
      const directSpend = added
        ? totalActualizedSpend - contributorsSpendSum
        : totalActualizedSpend + contributorsSpendSum;
      const directEffect = added
        ? totalActualizedEffect - contributorsEffectSum
        : totalActualizedEffect + contributorsEffectSum;
      const prevChannel = index > 0 ? barData[index - 1].channelName : undefined;
      const isFirstOfChannel = channelName && channelName !== prevChannel;

      return (
        <div
          className="legendItem"
          onClick={onClick ? () => onClick(contributors, name) : undefined}
          key={name}
        >
          {expandedModal && (
            <>
              {channelName ? (
                isFirstOfChannel ? (
                  <>
                    <div className="itemIcon">
                      <OverlayTrigger
                        placement={OverlayTrigger.PLACEMENTS.TOP.CENTER}
                        overlay={
                          <BootstrapTooltip id="dgDcIconTooltip">
                            {added ? "Demand Generation " : "Demand Capture "}
                            {/* TODO: format groupBy to pretty display name */}
                            {groupBy}
                          </BootstrapTooltip>
                        }
                      >
                        {added ? <DemandGenIcon /> : <DemandCaptureIcon />}
                      </OverlayTrigger>
                    </div>
                    <div className="channelText">
                      {capitalizeWords(channelName)}
                      {incrementality && groupBy === "channel" && (
                        <MdOutlineWarningAmber style={{ color: "orange", marginLeft: "5px" }} />
                      )}
                    </div>
                  </>
                ) : (
                  <div className="channelTextSpacer" />
                )
              ) : (
                <div className="nameText">
                  {displayName}
                  {incrementality && (
                    <MdOutlineWarningAmber style={{ color: "orange", marginLeft: "5px" }} />
                  )}
                </div>
              )}
              {groupBy !== "channel" && channelName && (
                <div className="platformTacticBrandText">
                  <div className="platformLabel">
                    {capitalizeWords(platformName)}
                    {incrementality && ["platform", "name"].includes(groupBy) && (
                      <MdOutlineWarningAmber style={{ color: "orange", marginLeft: "5px" }} />
                    )}
                  </div>
                  {groupBy === "name" && (
                    <div className="tacticBrandLabel">
                      {capitalizeWords(`${tacticName}, ${brandNonBrandName}`)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {!expandedModal && (
            <>
              <div className="itemIcon">{added ? <DemandGenIcon /> : <DemandCaptureIcon />}</div>
              <div className="nameText">{displayName}</div>
            </>
          )}
          <div className="itemValuesContainer">
            <div className="itemValue spend">
              <div className="valueLabel">Spend:</div>
              <div className="actualizedValue">{valueFormatter(totalActualizedSpend)}</div>
              {displayActualValues && (
                <div className="actualValue">{totalActualizedSpendRawValueLabel}</div>
              )}
              {expandedModal && (
                <>
                  <div className="verticalDivider" />
                  <div className="directValue">{valueFormatter(directSpend)}</div>
                </>
              )}
              {displayActualValues && (
                <div className="actualValue">{totalSpendForChannelLabel}</div>
              )}
            </div>
            <div className="itemValue effect">
              <div className="valueLabel">Effect:</div>
              <div className="actualizedValue">{valueFormatter(totalActualizedEffect)}</div>
              {displayActualValues && (
                <div className="actualValue">{totalActualizedEffectRawValueLabel}</div>
              )}
              {expandedModal && (
                <>
                  <div className="verticalDivider" />
                  <div className="directValue">{valueFormatter(directEffect)}</div>
                </>
              )}
              {displayActualValues && (
                <div className="actualValue">{totalEffectForChannelLabel}</div>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);
